import { toHTML } from "@portabletext/to-html";
import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent, useEffect, useState } from "react";
import { Conversion } from "@bluebottlecoffee/design-system";
import sanityClient from "../../../../lib/sanity-client";
import renderModule from "../../../../lib/render-module";
import {
  Product as ProductSchema,
  Config as ConfigSchema,
  ShippingCopy as ShippingCopySchema,
} from "../../../../lib/sanity-schema";
import {
  toConversionProps,
  ConversionSchema,
} from "../../../../lib/transformers/conversion";
import { useCart } from "../../../../lib/chord/packages/web/src/hooks/actions/use-cart";
import { useProduct } from "../../../../lib/chord/packages/web/src/hooks/actions/use-product";
import useVariantAvailability from "../../../../lib/chord/packages/web/src/hooks/components/use-variant-availability";
import {
  CartBasePropsWithSlug,
  cartQueries,
} from "../../../../lib/sanity/shared";
import { filters, getSlug } from "../../../../lib/sanity/utils";
import {
  cartVariants,
  getDefaultStaticPropsMiniCartWithSlug,
  hasOptionValues,
} from "../../../../lib/utils";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import { MiniCartDialog } from "../../../../components/MiniCartDialog";
import { useUser } from "../../../../lib/chord/packages/web/src/hooks/actions/use-user";
import { useAuth } from "../../../../lib/chord/packages/web/src/hooks/actions/use-auth";
import {
  buildSanityQuery,
  productFilters,
  shippingCopyQuery,
} from "../../../../lib/sanity/product-queries";
import { ChordUseCartReturnType } from "../../../../lib/chord/types";
import { useSiteWidePromo } from "../../../../lib/integrations/api/use-site-wide-promo";
import emailIsValid from "../../../../lib/utils/strings/email-is-valid";

interface PageData extends CartBasePropsWithSlug {
  conversion: ConversionSchema;
  shippingCopy: ShippingCopySchema;
}

type ConversionAndCopyType = {
  conversion: ConversionSchema;
  shippingCopy: ShippingCopySchema;
};

const addMetaTagForNoFollowProduct = (
  conversion: ConversionSchema,
  lang: string,
): void => {
  if (conversion.excludeFromIndex) {
    if (!Array.isArray(conversion.metaTags)) {
      // eslint-disable-next-line no-param-reassign
      conversion.metaTags = [];
    }

    conversion.metaTags.push({
      _type: "metaTag",
      _key: "",
      name: "robots",
      content: {
        _type: "localizedString",
        [lang]: "noindex,nofollow",
      },
    });
  }
};

function isValidConversion(p: ConversionSchema, lang: string): boolean {
  const test: Function = (field: string): boolean => {
    if (!p[field]) {
      if (p.name && p.name[lang]) {
        console.warn(`${p.name[lang]} does not have ${field}, skipping...`);
        return false;
      } else {
        throw Error(
          `Product with sanity id [${p._id}] is published and does not have a name in ${lang}.`,
        );
      }
    } else if (Array.isArray(p[field])) {
      if (p[field].length === 0) {
        if (p.name && p.name[lang]) {
          console.warn(`${p.name[lang]} has an empty ${field}, skipping...`);
          return false;
        } else {
          throw Error(
            `Product with sanity id [${p._id}] is published and does not have a name in ${lang}.`,
          );
        }
      }
    }

    return true;
  };

  // ensure require fields exist
  if (
    !test("slug") ||
    !test("description") ||
    !test("images") ||
    !test("variants") ||
    !hasOptionValues(p, lang)
  )
    return false;

  return true;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticPropsMiniCartWithSlug(
    context,
  );

  const { lang, slug } = defaultProps;

  // A Combined query for the product specific conversion module
  // and the shipping copy Document in Sanity
  const query = `{
    'conversion': ${buildSanityQuery(lang, slug)},
    'shippingCopy': ${shippingCopyQuery},
  }`;

  const conversionAndCopy = await sanityClient().fetch<ConversionAndCopyType>(
    query,
  );

  if (!isValidConversion(conversionAndCopy.conversion, lang))
    return { notFound: true };

  return {
    props: {
      ...defaultProps,
      ...conversionAndCopy,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const config = await sanityClient().fetch<ConfigSchema>(
    cartQueries.configQuery,
  );

  const productSlugs = await sanityClient().fetch<
    ProductSchema["slug"]["current"][]
  >(`*[
    ${filters.whereType("product")}
    && ${productFilters}
  ].${getSlug<ProductSchema>("slug")}`);

  const params = config.languages.flatMap((lang) =>
    productSlugs.map((slug) => ({
      params: {
        region: config.region,
        lang,
        slug,
      },
    })),
  );

  return {
    paths: params,
    fallback: false,
  };
};

const Page: FunctionComponent<PageData> = ({
  conversion: conversionProp,
  shippingCopy,
  ...miniCartLayoutWrapperProps
}) => {
  const [isWaitListed, setIsWaitListed] = useState<boolean>(false);
  const [waitListError, setWaitListError] = useState<boolean>(false);
  const [forceReturnValue, setForceReturnValue] = useState<boolean>(false);
  /**
   * giftCardFormCopy is only pulled once but used in multiple places
   * 1. MiniCartDialog
   * 2. GiftCardForm (in Conversion)
   */
  const { region, lang, giftCardFormCopy } = miniCartLayoutWrapperProps;
  const {
    cart: { data },
    addToCart,
    subscribeProduct,
    modifyGiftCards,
  }: ChordUseCartReturnType = useCart();
  const { createStockRequest } = useProduct();
  const { user, loadUser } = useUser();

  const conversion = useSiteWidePromo<ConversionSchema>(conversionProp, {
    forceReturnValue,
  });

  addMetaTagForNoFollowProduct(conversion, lang);

  const miniCartDialog = (
    <MiniCartDialog
      giftCardFormCopy={giftCardFormCopy}
      miniCartCopy={miniCartLayoutWrapperProps.cart}
      region={region}
      lang={lang}
    />
  );
  const lineItems = cartVariants({
    data,
    dialect: { region, lang },
  });

  async function handleCreateStockRequest({
    email,
    sku,
  }: {
    email: string;
    sku: string;
  }): Promise<void> {
    try {
      const response = await createStockRequest({ email, sku });
      if (response?.status === 201) {
        setIsWaitListed(true);
      } else {
        setWaitListError(true);
      }
    } catch (error) {
      setWaitListError(true);
      console.error(error);
    }
  }

  const { getToken } = useAuth();

  useEffect(() => {
    try {
      getToken();
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (!user.data.email) {
      try {
        loadUser();
      } catch (error) {
        console.error(error);
      }
    }
  }, [user.data]);

  return (
    <LayoutWrapper
      {...{
        ...miniCartLayoutWrapperProps,
        pageTitle: `${conversion.name[lang]} | Blue Bottle Coffee`,
        metaTags: conversion.metaTags,
        slideOutContent: [miniCartDialog],
      }}
    >
      <Conversion
        {...toConversionProps(conversion, lang)}
        onSubscriptionSelected={setForceReturnValue}
        staticShippingCopy={shippingCopy.staticShippingCopy[lang]}
        subscriptionBenefitsCopy={
          toHTML(shippingCopy.subscriptionBenefitsCopy?.[lang]) ?? ""
        }
        subscriptionPriceDisclaimerCopy={
          shippingCopy.subscriptionPriceDisclaimerCopy?.[lang]
        }
        addToCart={addToCart}
        modifyGiftCards={modifyGiftCards}
        lineItems={lineItems}
        createStockRequest={handleCreateStockRequest}
        giftCardFormCopy={giftCardFormCopy}
        subscribeProduct={subscribeProduct}
        useVariantAvailability={useVariantAvailability}
        isWaitListed={isWaitListed}
        waitListError={waitListError}
        waitListEmailDefault={user?.data.email}
        emailIsValid={emailIsValid}
      />
      {conversion.modules?.map((m) => renderModule(m, region, lang))}
    </LayoutWrapper>
  );
};

export default Page;
